import { useMutation, useQuery } from '@apollo/client';
import { Button, TBody, TD, TH, THead, TR, Table } from '@meshhub/design-tokens/ui';
import { ChevronDown, ChevronUp, Pencil, Plus } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import {
  ActiveBadge,
  CheckboxField,
  ConfirmDialog,
  FormDialog,
  LoadingOrEmpty,
  MutationError,
  SelectField,
  SettingsPage,
  ShowInactiveToggle,
  SystemLock,
  TextField,
} from '../../components/settings/SettingsKit.web.jsx';
import { formatCount } from '../../lib/format.js';
import {
  ADMIN_LOOKUPS_QUERY,
  CREATE_LOOKUP,
  REORDER_LOOKUPS,
  SET_LOOKUP_ACTIVE,
  UPDATE_LOOKUP,
} from '../../lib/queries.js';
import { S } from '../../lib/strings.js';

/** Specialties get their own hierarchy-aware screen. */
const KINDS = [
  'FIELD_OF_WORK',
  'COMMUNICATION_CHANNEL',
  'PRODUCT',
  'INTERACTION_TYPE',
  'TEMPLATE_CATEGORY',
  // Where a contact came from. The CRM has accepted this kind since migration
  // 0015 and the search workbench facets on it, but it was missing from this
  // list — so the only way a new congress entered the vocabulary was the
  // spreadsheet importer inventing one, and nobody could relabel it.
  'ACQUISITION_SOURCE',
  // The CRM profile vocabularies (migration 0016). Listed here for exactly the
  // reason ACQUISITION_SOURCE eventually was: a vocabulary the admin surface
  // cannot reach is a feature that does not exist, and the tests cannot see it
  // (CLAUDE.md §15).
  'LIFECYCLE_STAGE',
  'SALES_STAGE',
  'CUSTOMER_HEALTH',
  'ENGAGEMENT_STATUS',
  'CITY',
  'FACILITY_TYPE',
];

/**
 * Kinds whose rows can require the free-text detail beside them.
 *
 * A per-kind list rather than a checkbox on every vocabulary, because the flag
 * only means something where a paired text field exists — offering it on
 * «محصول» would be a control with nothing behind it. The RULE it drives is
 * still generic: the domain reads `requiresDetail` off the row and knows
 * nothing about clinics.
 */
const DETAIL_KINDS = new Set(['FACILITY_TYPE']);

export function LookupsScreen() {
  const [kind, setKind] = useState(KINDS[0]);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [editing, setEditing] = useState(null); // lookup | 'new' | null
  const [confirming, setConfirming] = useState(null);

  const variables = { kind, includeInactive };
  const { data, loading } = useQuery(ADMIN_LOOKUPS_QUERY, { variables });
  const refetch = [{ query: ADMIN_LOOKUPS_QUERY, variables }];

  const [createLookup, createState] = useMutation(CREATE_LOOKUP, { refetchQueries: refetch });
  const [updateLookup, updateState] = useMutation(UPDATE_LOOKUP, { refetchQueries: refetch });
  const [setActive, activeState] = useMutation(SET_LOOKUP_ACTIVE, { refetchQueries: refetch });
  const [reorder, reorderState] = useMutation(REORDER_LOOKUPS, { refetchQueries: refetch });

  const rows = useMemo(
    () => [...(data?.lookups ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [data],
  );

  const mutationError =
    createState.error || updateState.error || activeState.error || reorderState.error;

  /** Buttons rather than drag-and-drop: keyboard-reachable and unambiguous. */
  function move(index, delta) {
    const next = [...rows];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    reorder({ variables: { kind, orderedIds: next.map((r) => r.id) } });
  }

  return (
    <SettingsPage
      title={S.settings.sections.lookups}
      hint={S.settings.common.noDelete}
      toolbar={
        <>
          <ShowInactiveToggle checked={includeInactive} onChange={setIncludeInactive} />
          <Button variant="primary" onClick={() => setEditing('new')} className="gap-2">
            <Plus size={15} aria-hidden="true" />
            {S.settings.common.newItem}
          </Button>
        </>
      }
    >
      <div className="mb-5 flex items-center gap-3">
        <SelectField
          label={S.settings.lookups.kindLabel}
          value={kind}
          onChange={(event) => setKind(event.target.value)}
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {S.settings.lookups.kinds[k]}
            </option>
          ))}
        </SelectField>
        <span className="mt-5 text-sm text-text-muted">
          {S.contacts.count(formatCount(rows.length))}
        </span>
      </div>

      <MutationError error={mutationError} />
      <LoadingOrEmpty loading={loading && rows.length === 0} empty={!loading && rows.length === 0} />

      {rows.length > 0 && (
        <Table className="mt-4">
          <THead>
            <TR>
              <TH>{S.settings.common.code}</TH>
              <TH>{S.settings.common.labelFa}</TH>
              <TH>{S.settings.common.labelEn}</TH>
              <TH>{S.settings.common.sortOrder}</TH>
              <TH>{S.settings.common.status}</TH>
              <TH>{S.settings.common.actions}</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((row, index) => (
              <TR key={row.id} className={row.isActive ? '' : 'opacity-60'}>
                <TD dir="ltr" className="text-start font-medium">
                  {row.code}
                </TD>
                <TD>{row.labelFa}</TD>
                <TD dir="ltr" className="text-start text-text-muted">
                  {row.labelEn || S.app.none}
                </TD>
                <TD className="text-text-muted">{formatCount(row.sortOrder)}</TD>
                <TD>
                  <span className="flex items-center gap-2">
                    <ActiveBadge isActive={row.isActive} />
                    <SystemLock isSystem={row.isSystem} />
                  </span>
                </TD>
                <TD>
                  <span className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      aria-label={S.app.edit}
                      title={S.app.edit}
                      onClick={() => setEditing(row)}
                    >
                      <Pencil size={14} aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      aria-label={S.settings.common.moveUp}
                      title={S.settings.common.moveUp}
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                    >
                      <ChevronUp size={14} aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      aria-label={S.settings.common.moveDown}
                      title={S.settings.common.moveDown}
                      disabled={index === rows.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      <ChevronDown size={14} aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={row.isSystem && row.isActive}
                      title={row.isSystem && row.isActive ? S.settings.common.systemLocked : ''}
                      onClick={() => setConfirming(row)}
                    >
                      {row.isActive ? S.app.deactivate : S.app.activate}
                    </Button>
                  </span>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      {editing && (
        <LookupDialog
          kind={kind}
          showRequiresDetail={DETAIL_KINDS.has(kind)}
          lookup={editing === 'new' ? null : editing}
          busy={createState.loading || updateState.loading}
          onClose={() => setEditing(null)}
          onSubmit={async (values) => {
            if (editing === 'new') {
              await createLookup({ variables: { input: { kind, ...values } } });
            } else {
              await updateLookup({ variables: { input: { id: editing.id, ...values } } });
            }
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirming)}
        onOpenChange={(open) => !open && setConfirming(null)}
        title={confirming?.isActive ? S.app.deactivate : S.app.activate}
        message={
          confirming?.isActive
            ? S.settings.common.confirmDeactivate(confirming?.labelFa ?? '')
            : S.settings.common.confirmActivate(confirming?.labelFa ?? '')
        }
        danger={confirming?.isActive}
        confirmLabel={confirming?.isActive ? S.app.deactivate : S.app.activate}
        onConfirm={() =>
          setActive({ variables: { id: confirming.id, isActive: !confirming.isActive } })
        }
      />
    </SettingsPage>
  );
}

function LookupDialog({ kind, lookup, onClose, onSubmit, busy, showRequiresDetail }) {
  const isNew = !lookup;
  const [code, setCode] = useState(lookup?.code ?? '');
  const [labelFa, setLabelFa] = useState(lookup?.labelFa ?? '');
  const [labelEn, setLabelEn] = useState(lookup?.labelEn ?? '');
  const [sortOrder, setSortOrder] = useState(lookup?.sortOrder ?? 0);
  const [requiresDetail, setRequiresDetail] = useState(Boolean(lookup?.requiresDetail));

  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={isNew ? S.settings.lookups.createTitle : `${S.app.edit} — ${lookup.labelFa}`}
      busy={busy}
      onSubmit={() =>
        onSubmit({
          ...(isNew ? { code: code.trim() } : {}),
          labelFa,
          labelEn,
          sortOrder: Number(sortOrder),
          // Sent ONLY for a kind that pairs with a detail field. Absent leaves
          // the stored flag alone, which is what stops a relabel of any other
          // vocabulary from silently clearing it.
          ...(showRequiresDetail ? { requiresDetail } : {}),
        })
      }
    >
      <TextField
        label={S.settings.common.code}
        value={code}
        dir="ltr"
        required={isNew}
        disabled={!isNew}
        hint={isNew ? S.settings.lookups.codeHint : S.settings.common.codeImmutable}
        onChange={(event) => setCode(event.target.value.toUpperCase())}
      />
      <TextField
        label={S.settings.common.labelFa}
        value={labelFa}
        required
        onChange={(event) => setLabelFa(event.target.value)}
      />
      <TextField
        label={S.settings.common.labelEn}
        value={labelEn}
        dir="ltr"
        onChange={(event) => setLabelEn(event.target.value)}
      />
      <TextField
        label={S.settings.common.sortOrder}
        type="number"
        value={sortOrder}
        onChange={(event) => setSortOrder(event.target.value)}
      />
      {showRequiresDetail && (
        <CheckboxField
          label={S.settings.lookups.requiresDetail}
          checked={requiresDetail}
          onChange={(event) => setRequiresDetail(event.target.checked)}
        />
      )}
      {showRequiresDetail && (
        <p className="text-xs text-text-muted">{S.settings.lookups.requiresDetailHint}</p>
      )}
      {lookup?.isSystem && (
        <p className="text-xs text-text-muted">{S.settings.common.systemLocked}</p>
      )}
      <p className="text-xs text-text-muted">{S.settings.lookups.kinds[kind]}</p>
    </FormDialog>
  );
}
